import React from 'react';
import {
  Button, FormField, JalaliDatePicker, SearchInput, SegmentedControl,
} from '@parsina/framework';
import { X } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { useShortcut } from '../../lib/shortcuts';
import { SEARCH_DEBOUNCE_MS } from '../../constants/list';
import { OWNER_FILTERS } from '../../lib/demoRequestsView';

/**
 * Zone ④ — the queue's filters (§3.2 T1, DESIGN_CONCEPT §9.2).
 *
 * Owner (all / mine / unassigned) is a segmented FILTER, the creation window
 * is two Jalali pickers (the kit's `DateRangeFilter` has hardcoded Persian copy
 * and a physical popover edge — the same reason `LedgerView` composes two
 * pickers), and the search box narrows by name or contact point. Everything
 * applies immediately and lives in the URL; the search echoes every keystroke
 * and writes the URL after the debounce.
 *
 * `/` focuses the search, through the registry, so it is listed in `?`.
 */
export default function DemoRequestsToolbar({
  view, onChange, onClear, filtered, resultLabel, canFilterMine,
}) {
  const { t } = useTranslation();
  const searchRef = React.useRef(null);
  const [draft, setDraft] = React.useState(view.q || '');

  React.useEffect(() => { setDraft(view.q || ''); }, [view.q]);

  React.useEffect(() => {
    if (draft === (view.q || '')) return undefined;
    const timer = setTimeout(() => onChange({ q: draft, page: 1 }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, view.q, onChange]);

  const focusSearch = () => {
    searchRef.current?.focus();
    searchRef.current?.select?.();
  };
  useShortcut('/', focusSearch, {
    label: t('shortcuts.action.search'),
    group: t('shortcuts.groups.demoRequests'),
  });

  const owners = OWNER_FILTERS.map((value) => ({
    value,
    label: t(`demoRequests.filters.owner.${value}`),
    // «Mine» needs to know who «me» is; without `me` it cannot narrow.
    disabled: value === 'me' && !canFilterMine,
  }));

  return (
    <div data-testid="demo-requests-toolbar">
      <div className="mb-2 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
        <FormField label={t('demoRequests.filters.owner.label')}>
          <div className="flex h-11 items-center">
            <SegmentedControl
              semantics="filter"
              size="sm"
              ariaLabel={t('demoRequests.filters.owner.label')}
              options={owners}
              value={view.owner}
              onChange={(owner) => onChange({ owner, page: 1 })}
              data-testid="demo-requests-owner"
            />
          </div>
        </FormField>

        <FormField label={t('demoRequests.filters.from')}>
          <div className="w-44" data-testid="demo-requests-from">
            <JalaliDatePicker
              value={view.from || ''}
              onChange={(v) => onChange({ from: v || '', page: 1 })}
              placeholder={t('demoRequests.filters.datePlaceholder')}
              disableFuture
            />
          </div>
        </FormField>
        <FormField label={t('demoRequests.filters.to')}>
          <div className="w-44" data-testid="demo-requests-to">
            <JalaliDatePicker
              value={view.to || ''}
              onChange={(v) => onChange({ to: v || '', page: 1 })}
              placeholder={t('demoRequests.filters.datePlaceholder')}
              disableFuture
            />
          </div>
        </FormField>

        <FormField label={t('demoRequests.search.label')} style={{ flex: '1 1 16rem', minWidth: '16rem' }}>
          <SearchInput
            ref={searchRef}
            value={draft}
            onChange={(e) => setDraft(e.target?.value ?? e)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && draft) { e.stopPropagation(); setDraft(''); }
            }}
            placeholder={t('demoRequests.search.placeholder')}
            aria-label={t('demoRequests.search.label')}
            clearLabel={t('shortcuts.action.clear')}
            data-testid="demo-requests-search"
          />
        </FormField>

        {filtered ? (
          <div className="flex h-11 items-center">
            <Button variant="ghost" size="sm" onClick={onClear} data-testid="demo-requests-clear-filters">
              <X size={14} aria-hidden />
              {t('common.actions.clearFilters')}
            </Button>
          </div>
        ) : null}
      </div>

      <p className="mb-3 text-xs text-muted-foreground" aria-live="polite" data-testid="demo-requests-count">
        {resultLabel}
      </p>
    </div>
  );
}
